/**
 * @module services/api-upload/buildCustomizeSettingForApi
 * @description 接口上传统一入口：visibility 过滤 + convert + 可选补丁 + 空则 omit
 *
 * 不在此做切图 L2 reconcile（切图时 applyChartTypeLinkage 已处理）。
 *
 * 可选第二参数 options.patches：upload 前按场景修正 customizeSetting（如对比时间关闭后
 * 还原 column_sort 展示名），默认不开启，避免影响 Search / 保存等常规计算路径。
 */

import PANEL_LAYOUT_BY_CHART, {
  layoutToResolvedGroups,
} from '../../manifest/panelLayout';
import { evaluateVisibility } from '../../manifest/visibility/evaluateVisibility';
import { convert } from '../convert';
import { buildQBSnapshotFromColumns } from './qbSnapshot';
import { normalizeRecord } from './normalize';
import { applyCustomizeSettingPatches } from './applyCustomizeSettingPatches';
import { omitEmptyCustomizeSetting } from './omitEmptyCustomizeSetting';
import type {
  IBuildCustomizeSettingOptions,
  IPrepareConfigInput,
} from './types';
import type { TChartType } from '@dbl/core/chart';

function buildFormDelKeyForChartType(
  input: IPrepareConfigInput,
): Record<string, boolean> {
  const base = normalizeRecord(input.base);
  const draft = normalizeRecord(input.draft);
  const qb = buildQBSnapshotFromColumns({ ...input, base, draft });
  const tabs = PANEL_LAYOUT_BY_CHART[input.chartType] ?? [];
  return evaluateVisibility(
    qb,
    layoutToResolvedGroups(input.chartType, tabs),
  ).formDelKey;
}

/**
 * 接口上传统一入口。
 *
 * 流水线：normalize → visibility(formDelKey) → convert → applyCustomizeSettingPatches → omit 空字段
 *
 * @param input - base/draft + QB 列信息
 * @param options - 可选补丁配置；不传则与改造前行为完全一致
 * @returns 非空 customizeSetting；全无有效字段时返回 undefined
 *
 * @example
 * // 常规计算（无补丁）
 * buildCustomizeSettingForApi({ chartType, base, draft, columnDimensions, columnMetrics });
 *
 * @example
 * // VsIcon 关闭对比时间后立即重算
 * buildCustomizeSettingForApi(input, {
 *   patches: { columnSortCompareTime: true },
 *   columnFilters: newFilters,
 * });
 */
export function buildCustomizeSettingForApi(
  input: IPrepareConfigInput,
  options?: IBuildCustomizeSettingOptions,
): Record<string, unknown> | undefined {
  const base = normalizeRecord(input.base);
  const draft = normalizeRecord(input.draft);
  const hasDraft = normalizeRecord(input.hasDraft) as Record<string, boolean>;
  const formDelKey = buildFormDelKeyForChartType({ ...input, base, draft });

  const { customizeSetting } = convert({
    chartType: input.chartType,
    base,
    draft,
    hasDraft,
    formDelKey,
  });

  // 补丁阶段使用归一化后的 base/draft，与 convert 实际读取的数据一致
  const normalizedInput: IPrepareConfigInput = { ...input, base, draft };
  const patched = applyCustomizeSettingPatches(
    customizeSetting,
    normalizedInput,
    options,
  );

  return omitEmptyCustomizeSetting(patched);
}

/**
 * 组合图切换条：为多种 chartType 各构建切片后合并，空则 omit。
 *
 * options 会透传给每次 buildCustomizeSettingForApi 调用（各 chartType 共用同一套补丁配置）。
 */
export function mergeCustomizeSettingsForApi(
  chartTypes: TChartType[],
  shared: Omit<IPrepareConfigInput, 'chartType'>,
  options?: IBuildCustomizeSettingOptions,
): Record<string, unknown> | undefined {
  let merged: Record<string, unknown> | undefined;
  for (const chartType of chartTypes) {
    const slice = buildCustomizeSettingForApi(
      { ...shared, chartType },
      options,
    );
    if (slice) {
      merged = { ...(merged ?? {}), ...slice };
    }
  }
  return merged;
}

